import { api } from './api';
import { formatDateOnly, shiftDateOnly, todayInputValue } from './taskDates';

/**
 * One line on a day's list: a title and a tick, hung off a person and a date.
 *
 * `day` is a calendar date (`YYYY-MM-DD`); `doneAt`, `createdAt` and `updatedAt`
 * are instants. The split is the same one taskDates.ts exists to keep straight.
 */
export type DailyChecklistItem = {
  id: string;
  day: string;
  title: string;
  done: boolean;
  /** When it was ticked. Not rendered anywhere -- it is what orders the
   *  struck-out half of the list. See `sortDailyItems`. */
  doneAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** A day on the history page, as the server's group-by answers it. */
export type DailyChecklistDay = { day: string; total: number; done: number };

export const listDailyChecklist = (day: string, signal?: AbortSignal) =>
  api.get<{ day: string; items: DailyChecklistItem[] }>(
    `/api/daily-checklist?day=${day}`,
    signal,
  );

export const listDailyChecklistDays = (
  params: { before?: string; limit?: number } = {},
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams();
  if (params.before) query.set('before', params.before);
  if (params.limit) query.set('limit', String(params.limit));
  return api.get<{ days: DailyChecklistDay[]; nextBefore: string | null }>(
    `/api/daily-checklist/days?${query}`,
    signal,
  );
};

export const addDailyChecklistItem = (day: string, title: string) =>
  api.post<{ item: DailyChecklistItem }>('/api/daily-checklist', { day, title });

export const updateDailyChecklistItem = (
  itemId: string,
  patch: { title?: string; done?: boolean },
) => api.patch<{ item: DailyChecklistItem }>(`/api/daily-checklist/${itemId}`, patch);

export const deleteDailyChecklistItem = (itemId: string) =>
  api.del<{ ok: true }>(`/api/daily-checklist/${itemId}`);

/**
 * The server's ITEM_ORDER, restated so a tick can move its row in the same paint
 * as the click rather than after a round trip.
 *
 * This is the same move `qualifies` makes in useDayTasks, which restates the day
 * query's filter for the same reason. The two copies have to agree, or a ticked
 * row lands in one place and jumps to another when the reply arrives.
 *
 * Outstanding first in the order they were typed; ticked after in the order they
 * were ticked. `doneAt` ascending rather than descending is what makes a tick
 * append to the very bottom -- descending would slot each new tick at the top of
 * the struck-out block and shove everything under it down a row, so every tick
 * would reshuffle the whole bottom half.
 *
 * Compared as strings, never through `Date.parse`: these are fixed-width ISO
 * instants in UTC, so lexicographic order is chronological order -- the same
 * property `isOverdue` leans on for calendar dates.
 */
export function compareDailyItems(a: DailyChecklistItem, b: DailyChecklistItem): number {
  if (a.done !== b.done) return a.done ? 1 : -1;
  const doneAt = (a.doneAt ?? '').localeCompare(b.doneAt ?? '');
  if (doneAt !== 0) return doneAt;
  const created = a.createdAt.localeCompare(b.createdAt);
  if (created !== 0) return created;
  // TIMESTAMP(3) is millisecond-resolution and this list is built to be typed
  // straight down, so two lines can share a stamp.
  return a.id.localeCompare(b.id);
}

export const sortDailyItems = (items: DailyChecklistItem[]): DailyChecklistItem[] =>
  [...items].sort(compareDailyItems);

/**
 * The stamp an optimistic tick carries until the server answers with its own.
 *
 * Deliberately not a bare `new Date().toISOString()`. The rows already ticked
 * carry the *server's* clock, so a browser running a few minutes behind would
 * stamp the newest tick earlier than an older one and sort it above -- a row
 * that visibly moves twice on a single click, on the one interaction this whole
 * card exists for. Taking the later of now and a millisecond past the latest
 * stamp on the list wins the bottom slot whatever the two clocks believe, and
 * the reply overwrites it either way.
 */
export function nextDoneAt(items: DailyChecklistItem[]): string {
  const latest = items.reduce(
    (max, item) => (item.doneAt && item.doneAt > max ? item.doneAt : max),
    '',
  );
  const now = new Date().toISOString();
  return now > latest ? now : new Date(Date.parse(latest) + 1).toISOString();
}

/** Optimistic rows, told apart from saved ones by their id -- the same trick
 *  `nextPendingTaskId` plays for tasks and the task checklist. */
let pendingCount = 0;
const PENDING_PREFIX = 'pending-daily:';
export const nextPendingItemId = (): string => `${PENDING_PREFIX}${(pendingCount += 1)}`;
export const isPendingItem = (id: string): boolean => id.startsWith(PENDING_PREFIX);

/** "Today" and "Yesterday" by name, anything older by date. Two names is the
 *  right number: "2 days ago" is slower to read than the date it replaces. */
export function dayLabel(day: string, today: string): string {
  if (day === today) return 'Today';
  if (day === shiftDateOnly(today, -1)) return 'Yesterday';
  return formatDateOnly(day);
}

/** The longer form the history page uses, where every row is a date and the
 *  weekday is what tells one from another at a glance. */
export function longDayLabel(day: string, today = todayInputValue()): string {
  if (day === today) return 'Today';
  if (day === shiftDateOnly(today, -1)) return 'Yesterday';
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return day;
  return new Date(year, month - 1, date).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}
